import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { context, trace } from '@opentelemetry/api';
import { env } from '@/env';
import { QflyError, toProblem } from './problem';

/**
 * Builds a W3C `traceparent` header value from the current OTel active span.
 *
 * Auto-instrumentation (see `shared/observability/otel.ts`) already injects
 * the header on fetch/XHR calls, but axios uses its own adapter so we add
 * the header explicitly to guarantee backend-stitched traces.
 */
const currentTraceparent = (): string | undefined => {
  const span = trace.getSpan(context.active());
  if (!span) return undefined;
  const ctx = span.spanContext();
  if (!ctx.traceId || !ctx.spanId) return undefined;
  const flags = (ctx.traceFlags ?? 0).toString(16).padStart(2, '0');
  return `00-${ctx.traceId}-${ctx.spanId}-${flags}`;
};

/**
 * Hook fired when a 401 survives the silent-refresh attempt below.
 * Wired by AuthProvider to clear local user state and bounce to login.
 */
let on401Handler: (() => void) | null = null;
export const setOn401Handler = (handler: (() => void) | null): void => {
  on401Handler = handler;
};

/**
 * Current SPA screen key, sent as `X-Qfly-Screen` so audited actions record
 * which page they originated from. Derived from the route path only (never the
 * query string / hash, which can carry ids or PII), charset-restricted, and
 * length-capped — the value lands in the never-purged audit_log and is shown to
 * admins, so it must be a safe, bounded token. The backend re-sanitises it too.
 */
const _SCREEN_MAX = 64;
const currentScreenHeader = (): string => {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
  const cleaned = Array.from(path)
    .filter((c) => /[A-Za-z0-9._/-]/.test(c))
    .join('')
    .slice(0, _SCREEN_MAX);
  return cleaned || 'root';
};

/**
 * In-flight refresh promise — when multiple requests 401 at the same time
 * (typical on page load with parallel fetches) they share a single POST
 * /auth/refresh instead of stampeding Cognito with N copies.
 */
let inFlightRefresh: Promise<void> | null = null;

/** Internal flag we tack onto retried configs so we don't loop on persistent 401s. */
type RetryableConfig = InternalAxiosRequestConfig & { _qflyRetried?: boolean };

export const createApiClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: env.apiBaseUrl,
    withCredentials: true,
    timeout: 30_000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    // FastAPI accepts repeated query keys for `list[T]` params — `?k=a&k=b` —
    // NOT the bracketed form axios emits by default (`?k[]=a&k[]=b`). Without
    // this override the server silently drops list filters because it sees
    // an unknown `k[]` key instead of the expected `k`. Affects forecast
    // stage_ids, region_ids, client_ids, owner_ids, category, etc.
    paramsSerializer: { indexes: null },
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (config.headers) {
      const traceparent = currentTraceparent();
      if (traceparent) config.headers.set('traceparent', traceparent);
      config.headers.set('X-Qfly-Screen', currentScreenHeader());
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error.response?.status ?? 0;
      const config = error.config as RetryableConfig | undefined;

      // Silent refresh: when a normal request 401s, try ONE round-trip
      // against POST /auth/refresh and replay the original call. The
      // refresh endpoint rotates the access-token cookie in place, so
      // the replayed request goes out with fresh credentials.
      //
      // Guards:
      //   - Don't retry the refresh call itself (would infinite-loop).
      //   - Don't retry a request we've already retried once.
      //   - Don't retry when there's no AxiosRequestConfig to replay.
      //   - Coalesce concurrent refresh attempts into one in-flight call.
      // Auth endpoints that must never be replayed: /auth/refresh would
      // infinite-loop; /auth/callback codes are single-use — replaying
      // after a failed exchange would always 401 again on the consumed code.
      // /auth/login and /auth/login/mfa (2026-08 native-login) 401 on wrong
      // credentials/code — there is no session to refresh yet, so a silent-
      // refresh attempt would always fail too, just adding a wasted round
      // trip before the real error reaches the caller. The same applies to the
      // self-service reset routes (/auth/forgot-password, /auth/reset-password):
      // a wrong/expired reset code returns 401 with no session, so without this
      // guard the silent-refresh path would fire on/auth/refresh AND trip the
      // global on401Handler (app-wide logout) on a mere bad-code entry.
      const isNonRetriableAuth = typeof config?.url === 'string'
        && (
          config.url.includes('/auth/refresh')
          || config.url.includes('/auth/callback')
          || config.url.includes('/auth/login')
          || config.url.includes('/auth/forgot-password')
          || config.url.includes('/auth/reset-password')
        );
      // Don't attempt a silent refresh while the OAuth exchange is in progress —
      // no session or refresh cookie exists yet, so the attempt always fails.
      const cbPath = window.location.pathname.replace(/\/+$/, '');
      const onCallbackPage = cbPath === '/auth/callback' || cbPath === '/callback';
      if (status === 401 && config && !config._qflyRetried && !isNonRetriableAuth && !onCallbackPage) {
        config._qflyRetried = true;
        try {
          if (!inFlightRefresh) {
            inFlightRefresh = instance
              .post('/auth/refresh')
              .then(() => undefined)
              .finally(() => {
                inFlightRefresh = null;
              });
          }
          await inFlightRefresh;
          return instance.request(config);
        } catch {
          // refresh failed — fall through to the original 401 handling
        }
      }

      if (status === 401 && on401Handler) {
        on401Handler();
      }
      const problem = toProblem(error.response?.data ?? error.message, status);
      return Promise.reject(new QflyError(problem));
    },
  );

  return instance;
};

/** Default shared singleton; components and hooks import this directly. */
export const apiClient: AxiosInstance = createApiClient();
